import { ThemeType } from 'types';
import { hexToRGB } from 'utils';
import { darkColorsTheme, antDarkColorsTheme } from './dark/darkTheme';
import { lightColorsTheme } from './light/lightTheme';

export const themeObject = {
  light: lightColorsTheme,
  dark: darkColorsTheme,
};

export const antThemeObject = {
  light: {},
  dark: antDarkColorsTheme,
};

type ThemeCssVariables = Record<`--${string}`, string>;

const getThemeCssVariables = (theme: ThemeType): ThemeCssVariables => {
  const colors = themeObject[theme];

  return {
    '--brand-primary-color': colors.semantic.brandPrimary,
    '--brand-primary-hover-color': colors.semantic.brandPrimaryHover,
    '--brand-accent-color': colors.semantic.brandAccent,
    '--navigation-surface-color': colors.semantic.navigationSurface,
    '--navigation-text-color': colors.semantic.navigationText,
    '--surface-page-color': colors.semantic.surfacePage,
    '--surface-container-color': colors.semantic.surfaceContainer,
    '--surface-elevated-color': colors.semantic.surfaceElevated,
    '--surface-muted-color': colors.semantic.surfaceMuted,
    '--surface-hover-color': colors.semantic.surfaceHover,
    '--surface-selected-color': colors.semantic.surfaceSelected,
    '--surface-disabled-color': colors.semantic.surfaceDisabled,
    '--text-primary-color': colors.semantic.textPrimary,
    '--text-secondary-content-color': colors.semantic.textSecondary,
    '--text-muted-color': colors.semantic.textMuted,
    '--text-disabled-color': colors.semantic.textDisabled,
    '--text-on-brand-color': colors.semantic.textOnBrand,
    '--text-on-light-color': colors.semantic.textOnLight,
    '--border-subtle-color': colors.semantic.borderSubtle,
    '--border-strong-color': colors.semantic.borderStrong,
    '--focus-ring-color': colors.semantic.focusRing,
    '--overlay-scrim-color': colors.semantic.overlayScrim,
    '--shadow-elevated': colors.semantic.shadowElevated,
    '--shadow-overlay': colors.semantic.shadowOverlay,
    '--status-success-text-color': colors.semantic.statusSuccessText,
    '--status-success-surface-color': colors.semantic.statusSuccessSurface,
    '--status-success-border-color': colors.semantic.statusSuccessBorder,
    '--status-warning-text-color': colors.semantic.statusWarningText,
    '--status-warning-surface-color': colors.semantic.statusWarningSurface,
    '--status-warning-border-color': colors.semantic.statusWarningBorder,
    '--status-error-text-color': colors.semantic.statusErrorText,
    '--status-error-surface-color': colors.semantic.statusErrorSurface,
    '--status-error-border-color': colors.semantic.statusErrorBorder,
    '--status-info-text-color': colors.semantic.statusInfoText,
    '--status-info-surface-color': colors.semantic.statusInfoSurface,
    '--status-info-border-color': colors.semantic.statusInfoBorder,
    '--highlight-surface-color': colors.semantic.highlightSurface,
    '--highlight-text-color': colors.semantic.highlightText,
    '--auth-panel-background-color': colors.semantic.authPanelBackground,
    '--schedule-tag-background-color': colors.semantic.scheduleTagBackground,
    '--export-action-color': colors.semantic.exportAction,
    '--primary-color': colors.primary,
    '--primary1-color': colors.primary1,
    '--primary-gradient-color': colors.primaryGradient,
    '--info-color': colors.primary,
    '--secondary-color': colors.secondary,
    '--error-color': colors.error,
    '--medium-error-color': colors.mediumErrorColor,
    '--dark-error-color': colors.darkError,
    '--warning-color': colors.warning,
    '--success-color': colors.success,
    '--background-color': colors.background,
    '--secondary-background-color': colors.secondaryBackground,
    '--secondary-background-selected-color': colors.secondaryBackgroundSelected,
    '--additional-background-color': colors.additionalBackground,
    '--collapse-background-color': colors.collapseBackground,
    '--timeline-background-color': colors.timelineBackground,
    '--spinner-base-color': colors.spinnerBase,
    '--sider-background-color': colors.siderBackground,
    '--shadow-color': colors.shadow,
    '--border-color': colors.border,
    '--border-nft-color': colors.borderNft,
    '--scroll-color': colors.scroll,
    '--primary-rgb-color': hexToRGB(colors.primary),
    '--info-rgb-color': hexToRGB(colors.primary),
    '--secondary-rgb-color': hexToRGB(colors.secondary),
    '--error-rgb-color': hexToRGB(colors.error),
    '--warning-rgb-color': hexToRGB(colors.warning),
    '--success-rgb-color': hexToRGB(colors.success),
    '--background-rgb-color': hexToRGB(colors.background),
    '--text-main-color': colors.textMain,
    '--text-light-color': colors.textLight,
    '--text-superLight-color': colors.textSuperLight,
    '--text-secondary-color': colors.textSecondary,
    '--text-dark-color': colors.textDark,
    '--text-nft-light-color': colors.textNftLight,
    '--text-sider-primary-color': colors.textSiderPrimary,
    '--text-sider-secondary-color': colors.textSiderSecondary,
    '--subtext-color': colors.subText,
    '--dashboard-map-background-color': colors.dashboardMapBackground,
    '--dashboard-map-circle-color': colors.dashboardMapCircleColor,
    '--dashboard-map-control-disabled-background-color':
      colors.dashboardMapControlDisabledBackground,
    '--notification-success-color': colors.notificationSuccess,
    '--notification-primary-color': colors.notificationPrimary,
    '--notification-warning-color': colors.notificationWarning,
    '--notification-error-color': colors.notificationError,
    '--icon-color': colors.icon,
    '--icon-hover-color': colors.iconHover,
    '--box-shadow': colors.boxShadow,
    '--box-shadow-hover': colors.boxShadowHover,
    '--box-shadow-nft-color': colors.boxShadowNft,
    '--box-shadow-nft-secondary-color': colors.boxShadowNftSecondary,
    '--heading-color': colors.heading,
    '--item-hover-bg': colors.itemHoverBg,
    '--background-base-color': colors.backgroundColorBase,
    '--border-base-color': colors.borderBase,
    '--disabled-color': colors.disable,
    '--disabled-bg-color': colors.disabledBg,
    '--layout-body-bg-color': colors.layoutBodyBg,
    '--layout-header-bg-color': colors.layoutHeaderBg,
    '--layout-sider-bg-color': colors.layoutSiderBg,
    '--input-placeholder-color': colors.inputPlaceholder,
    '--avatar-bg': colors.avatarBg,
    '--alert-text-color': colors.alertTextColor,
    '--breadcrumb-color': colors.breadcrumb,
    '--ant-primary-1': 'var(--primary1-color)',
  };
};

const darkOnlyCssVariables: ThemeCssVariables = {
  '--ant-success-color-deprecated-bg': antDarkColorsTheme.successBg,
  '--ant-success-color-deprecated-border': antDarkColorsTheme.successBorder,
};

export const applyThemeCssVariables = (
  theme: ThemeType,
  root: HTMLElement = document.documentElement
): void => {
  Object.keys(darkOnlyCssVariables).forEach((property) => {
    root.style.removeProperty(property);
  });

  const variables = {
    ...getThemeCssVariables(theme),
    ...(theme === 'dark' ? darkOnlyCssVariables : {}),
  };

  Object.entries(variables).forEach(([property, value]) => {
    root.style.setProperty(property, value);
  });
};
